import { useEffect, useState } from "react";
import { Hash, Bell, BellOff, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Avatar } from "@/components/common";
import { ConnectButton } from "@/components/ConnectButton";

function SpaceRow({ space }) {
  const [sub, setSub] = useState(space.subscribed);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/spaces/${space.slug}/subscribe`);
      setSub(r.data.subscribed);
      window.dispatchEvent(new Event("spaces-changed"));
    } catch (e) {} finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 border border-border rounded-lg p-3" data-testid={`starter-space-${space.slug}`}>
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary text-cobalt shrink-0"><Hash size={16} /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{space.name}</div>
        <div className="text-xs text-muted-foreground truncate">{space.description}</div>
      </div>
      <button onClick={toggle} disabled={busy} data-testid={`starter-subscribe-${space.slug}`}
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 shrink-0 ${
          sub ? "border border-border text-foreground" : "bg-cobalt text-white hover:bg-gold-hover"}`}>
        {sub ? <><BellOff size={13} /> Subscribed</> : <><Bell size={13} /> Subscribe</>}
      </button>
    </div>
  );
}

export function StarterPack({ onDone }) {
  const [spaces, setSpaces] = useState([]);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    api.get("/spaces").then((r) => {
      const notSub = (r.data || []).filter((s) => !s.subscribed && !s.premium)
        .sort((a, b) => (b.subscriber_count || 0) - (a.subscriber_count || 0));
      setSpaces(notSub.slice(0, 3));
    }).catch(() => {});
    api.get("/matches").then((r) => setPeople((r.data.matches || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="fade-up">
      <div className="inline-flex items-center gap-2 eyebrow text-cobalt mb-2"><Sparkles size={14} /> Your starter pack</div>
      <h1 className="font-serif font-extrabold text-3xl sm:text-4xl tracking-tight">Make Ikigora feel like home</h1>
      <p className="mt-2 text-sm text-muted-foreground">Subscribe to a few spaces and say hi to people you'll click with — you can always change this later.</p>

      {spaces.length > 0 && (
        <div className="mt-8">
          <div className="text-sm font-medium mb-3">Spaces to follow</div>
          <div className="space-y-2" data-testid="starter-spaces">
            {spaces.map((s) => <SpaceRow key={s.space_id} space={s} />)}
          </div>
        </div>
      )}

      {people.length > 0 && (
        <div className="mt-8">
          <div className="text-sm font-medium mb-3">People you should meet</div>
          <div className="grid sm:grid-cols-3 gap-3" data-testid="starter-people">
            {people.map((m) => (
              <div key={m.user_id} className="border border-border rounded-lg p-4 flex flex-col items-center text-center" data-testid={`starter-person-${m.user_id}`}>
                <Avatar src={m.picture} name={m.name} size={48} className="border" />
                <div className="mt-2 text-sm font-semibold truncate max-w-full">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate max-w-full">{(m.role?.length ? m.role.join(", ") : "") || m.title || "Member"}</div>
                <div className="mt-3"><ConnectButton userId={m.user_id} name={m.name}
                  suggestedNote={`Hi ${m.name.split(" ")[0]} — just joined Ikigora and would love to connect!`} size="sm" /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 flex justify-end">
        <button onClick={onDone} data-testid="starter-enter-button"
          className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-6 py-3 text-sm font-semibold hover:bg-gold-hover transition-colors">
          Enter Ikigora <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
